import { match } from '@frontend/duck-tape';
import { accountCreationSchemaLegacyDefaultValues, useAccountCreationFormLegacy } from '@frontend/forms';
import { signUpRouteValidator } from '@frontend/member-route-validators';
import { useEffect, useFeatureIsOn } from '@frontend/react';
import { useDefaultPromoCode, usePromotionTranslation } from '@frontend/react-domain';
import { AppShell, LoadingSpinnerPanel, useBreakpoints } from '@frontend/web-react';
import { setOnboardingStep } from '@frontend/web-utils';
import { AppContentWrapper, SignUpLanding, UnauthenticatedAppHeader } from '@frontend/web/components';
import { CreatePassword, UserInfo } from '@frontend/web/components/DomainSpecific/AcceptInvite';
import {
  useAnalytics,
  useAuthentication,
  useLazyRetrieveInviteQuery,
  useLazyRetrievePromotionDetailsQuery,
  useRetrieveClientQuery,
  useRetrieveWaitlistStatusQuery,
} from '@frontend/web/hooks';
import { useSignUpFlow } from '../hooks/useSignUpFlow';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { AUTHENTICATED_HOME_ROUTE, createFileRoute, handleMutation, redirect } from '../utils';

const signUpSteps = ['userInfo', 'createPassword'] as const;

/**
 * Legacy signup wizard component (flag OFF)
 * Uses the two-step wizard flow: UserInfo → CreatePassword
 * Collects birthday and phone during signup
 */
const LegacySignUpWizard = () => {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const { isMobile, isTablet } = useBreakpoints();
  const { track } = useAnalytics();
  const { currentStep, decrementStep, incrementStep, setCurrentStep } = useStepNavigation(signUpSteps, 'userInfo');
  const { isAuthenticated } = useAuthentication();

  const { data: waitlistStatus, isLoading: isWaitlistStatusLoading } = useRetrieveWaitlistStatusQuery();
  const retrieveInviteHookResult = useLazyRetrieveInviteQuery();
  const [, { data: inviteDetails, isLoading: isRetrieveInviteLoading }] = retrieveInviteHookResult;

  const { handleSubmitForm, isAnyLoading: isSignUpFlowLoading } = useSignUpFlow({
    inviteData: inviteDetails,
    navigateOnSuccess: navigate,
    onErrorSetStep: setCurrentStep,
    searchParams,
    type: 'signup',
  });

  const retrieveClientHookResult = useRetrieveClientQuery();
  const retrievePromotionDetailsHookResult = useLazyRetrievePromotionDetailsQuery();

  const defaultPromoCode = useDefaultPromoCode({
    overridePromoCode: searchParams?.promo,
    retrieveClientHookResult,
  });

  const { actions: promotionTranslationActions, state: promotionTranslationState } = usePromotionTranslation({
    defaultPromoCode,
    handleMutation,
    retrievePromotionDetailsHookResult,
  });

  const form = useAccountCreationFormLegacy({
    initialValues: accountCreationSchemaLegacyDefaultValues,
    onSubmit: handleSubmitForm,
  });

  const { setFieldValue } = form;

  useEffect(() => {
    setFieldValue('promoCode', promotionTranslationState.promoCode ?? '');
  }, [promotionTranslationState.promoCode, setFieldValue]);

  // Handle invite details prefilling
  useEffect(() => {
    if (inviteDetails) {
      setFieldValue('email', inviteDetails.email || '');
      setFieldValue('firstName', inviteDetails.firstName || '');
      setFieldValue('lastName', inviteDetails.lastName || '');
      setFieldValue('phoneNumber', inviteDetails.phoneNumber || '');
      setFieldValue('nickname', inviteDetails.nickname || '');
      setFieldValue('birthday', inviteDetails.birthday || ('' as DateISOString));
    }
  }, [inviteDetails, setFieldValue]);

  const isInitDataLoading = isRetrieveInviteLoading || isWaitlistStatusLoading;
  const isAnyLoading = isInitDataLoading || isSignUpFlowLoading;

  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/app' });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (waitlistStatus?.isWaitlistOn) {
      navigate({ search: searchParams, to: '/wait-list' });
    }
  }, [waitlistStatus, navigate, searchParams]);

  useEffect(() => {
    track({
      data: {
        entryType: 'signup',
        flowVariant: 'legacy',
        platform: 'web',
        step: currentStep,
      },
      event: 'signupStepViewed',
      topic: 'auth',
    });
  }, [currentStep, track]);

  useEffect(() => {
    setOnboardingStep(`web_legacy_${currentStep}`);
  }, [currentStep]);

  return (
    <AppShell>
      <UnauthenticatedAppHeader isHidden={isMobile || isTablet} />
      <AppContentWrapper className="flex-1">
        {isInitDataLoading ? (
          <LoadingSpinnerPanel fullScreen />
        ) : (
          match(currentStep)
            .with('userInfo', () => (
              <UserInfo
                disabledFields={{
                  email: !!inviteDetails?.email,
                  phoneNumber: !!inviteDetails?.phoneNumber,
                }}
                form={form}
                incrementStep={incrementStep}
                inviteDetails={inviteDetails}
                isAnyLoading={isAnyLoading}
              />
            ))
            .with('createPassword', () => (
              <CreatePassword
                decrementStep={decrementStep}
                form={form}
                inviteDetails={inviteDetails}
                isAnyLoading={isAnyLoading}
                promotionTranslationActions={promotionTranslationActions}
                promotionTranslationState={promotionTranslationState}
                searchParamsDescription={searchParams?.description}
                searchParamsPromoCode={searchParams?.promo}
              />
            ))
            .exhaustive()
        )}
      </AppContentWrapper>
    </AppShell>
  );
};

/**
 * New signup flow component (flag ON)
 * Uses SignUpLanding with SSO options and multi-route flow
 */
const NewSignUpFlow = () => {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const { isMobile, isTablet } = useBreakpoints();
  const { isAuthenticated, isClientSubscribed, isLoading } = useAuthentication();
  const { data: waitlistStatus, isLoading: isWaitlistStatusLoading } = useRetrieveWaitlistStatusQuery();

  useEffect(() => {
    setOnboardingStep('web_new_landing');
  }, []);

  useEffect(() => {
    if (waitlistStatus?.isWaitlistOn) {
      navigate({ search: searchParams, to: '/wait-list' });
    }
  }, [waitlistStatus, navigate, searchParams]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isClientSubscribed && isAuthenticated) {
      navigate({ to: '/app/home' });
    }
  }, [isLoading]);

  return (
    <AppShell>
      <UnauthenticatedAppHeader isHidden={isMobile || isTablet} />
      <AppContentWrapper className="flex-1">
        {isWaitlistStatusLoading ? <LoadingSpinnerPanel fullScreen /> : <SignUpLanding searchParams={searchParams} />}
      </AppContentWrapper>
    </AppShell>
  );
};

/**
 * Sign-up route component with feature flag support.
 *
 * When is_signup_changes_enabled is OFF (default):
 * - Renders legacy two-step wizard (UserInfo → CreatePassword)
 * - Collects birthday and phone during signup
 * - Password requires 12+ characters
 *
 * When is_signup_changes_enabled is ON:
 * - Renders SignUpLanding with SSO options
 * - Defers birthday/phone to post-signup onboarding
 * - Password requires 8+ chars with 1 number
 */
const SignUp = () => {
  const { enabled: isSignupChangesEnabled } = useFeatureIsOn('is_signup_changes_enabled');
  if (isSignupChangesEnabled) {
    return <NewSignUpFlow />;
  }

  return <LegacySignUpWizard />;
};

export const Route = createFileRoute('/sign-up')({
  beforeLoad: async ({ context }) => {
    if (context.authentication?.isAuthenticated) {
      throw redirect({ to: AUTHENTICATED_HOME_ROUTE });
    }
  },
  component: SignUp,
  validateSearch: signUpRouteValidator,
});
